import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import BlockRenderer from "../components/BlockRenderer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  ArrowLeft, Plus, Save, Loader2, Globe, ChevronUp, ChevronDown, Trash2, Copy,
  Type, AlignLeft, Image as ImageIcon, MousePointerClick, Video, FormInput, Timer, Minus, MoveVertical, FileText,
} from "lucide-react";
import { toast } from "sonner";

const genId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
const BLOCKS = [
  { type: "heading", label: "Heading", icon: Type },
  { type: "text", label: "Text", icon: AlignLeft },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "button", label: "Button", icon: MousePointerClick },
  { type: "video", label: "Video", icon: Video },
  { type: "form", label: "Opt-in Form", icon: FormInput },
  { type: "countdown", label: "Countdown", icon: Timer },
  { type: "divider", label: "Divider", icon: Minus },
  { type: "spacer", label: "Spacer", icon: MoveVertical },
];
const defaults = {
  heading: { text: "Your Big Headline", align: "center", size: "xl" },
  text: { text: "A supporting line that builds desire.", align: "center" },
  image: { url: "", alt: "", width: 640 },
  button: { label: "Get Started", action: "link", url: "#", align: "center", size: "lg" },
  video: { url: "" },
  form: { title: "Get Instant Access", submit_label: "Get Access", tag_on_submit: "opt-in", redirect_slug: "",
          fields: [{ key: "name", label: "Name", type: "text", required: true }, { key: "email", label: "Email", type: "email", required: true }] },
  countdown: { minutes: 15, label: "Offer ends in" },
  divider: {},
  spacer: { height: 40 },
};

export default function FunnelBuilder() {
  const { funnelId } = useParams();
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [pageId, setPageId] = useState(null);
  const [selBlock, setSelBlock] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/funnels/${funnelId}`);
    setFunnel(data);
    setPageId((prev) => prev || data.pages[0]?.id);
  }, [funnelId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/funnels/${funnelId}/analytics`).then(({ data }) => setAnalytics(data)).catch(() => {}); }, [funnelId, funnel?.status]);

  if (!funnel) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const page = funnel.pages.find((p) => p.id === pageId) || funnel.pages[0];
  const setPages = (pages) => setFunnel({ ...funnel, pages });
  const updatePage = (patch) => setPages(funnel.pages.map((p) => (p.id === page.id ? { ...p, ...patch } : p)));
  const setBlocks = (blocks) => updatePage({ blocks });

  const addPage = () => {
    const n = funnel.pages.length + 1;
    const np = { id: genId(), name: `Page ${n}`, slug: `page-${n}`, type: "landing", seo: { title: `Page ${n}`, description: "", og_image: "" }, settings: { bg: "#0b1220" }, blocks: [] };
    setPages([...funnel.pages, np]); setPageId(np.id);
  };
  const delPage = (id) => {
    if (funnel.pages.length <= 1) return toast.error("A funnel needs at least one page");
    const rest = funnel.pages.filter((p) => p.id !== id);
    setPages(rest); if (pageId === id) setPageId(rest[0].id);
  };
  const movePage = (idx, dir) => {
    const pages = [...funnel.pages]; const j = idx + dir;
    if (j < 0 || j >= pages.length) return;
    [pages[idx], pages[j]] = [pages[j], pages[idx]]; setPages(pages);
  };

  const addBlock = (type) => {
    const b = { id: genId(), type, props: { ...defaults[type] } };
    setBlocks([...(page.blocks || []), b]); setSelBlock(b.id);
  };
  const updateBlock = (id, props) => setBlocks(page.blocks.map((b) => (b.id === id ? { ...b, props: { ...b.props, ...props } } : b)));
  const moveBlock = (idx, dir) => {
    const arr = [...page.blocks]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; setBlocks(arr);
  };
  const delBlock = (id) => { setBlocks(page.blocks.filter((b) => b.id !== id)); if (selBlock === id) setSelBlock(null); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/funnels/${funnelId}`, { name: funnel.name, pages: funnel.pages, settings: funnel.settings });
      toast.success("Funnel saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const togglePublish = async () => {
    await save();
    const { data } = await api.post(`/funnels/${funnelId}/publish`);
    setFunnel({ ...funnel, status: data.status });
    toast.success(data.status === "published" ? "Funnel is live!" : "Funnel unpublished");
  };
  const copyLink = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/f/${funnelId}`);
    toast.success("Public link copied");
  };

  const block = page.blocks?.find((b) => b.id === selBlock);
  const brand = "#6366F1";

  return (
    <div className="animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/funnels")} className="text-muted-foreground hover:text-foreground" data-testid="back-to-funnels"><ArrowLeft className="w-5 h-5" /></button>
          <Input value={funnel.name} onChange={(e) => setFunnel({ ...funnel, name: e.target.value })}
            className="text-xl font-display font-bold h-auto py-1 border-0 bg-transparent px-0 focus-visible:ring-0 w-auto" data-testid="funnel-title-input" />
          <Badge variant="secondary" className={funnel.status === "published" ? "text-emerald-400" : "text-muted-foreground"}>{funnel.status === "published" ? "Live" : "Draft"}</Badge>
        </div>
        <div className="flex items-center gap-2">
          {funnel.status === "published" && <Button variant="outline" size="sm" className="gap-1.5" onClick={copyLink} data-testid="copy-link-button"><Copy className="w-4 h-4" />Link</Button>}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={save} disabled={saving} data-testid="save-funnel-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save</Button>
          <Button size="sm" className="gap-1.5 font-semibold" onClick={togglePublish} data-testid="publish-button">
            <Globe className="w-4 h-4" />{funnel.status === "published" ? "Unpublish" : "Publish"}</Button>
        </div>
      </div>

      {analytics && (
        <div className="grid grid-cols-3 gap-3 mb-6 max-w-lg">
          <Stat label="Visits" value={analytics.total_visits} />
          <Stat label="Opt-ins" value={analytics.total_submissions} />
          <Stat label="Opt-in rate" value={`${analytics.opt_in_rate}%`} />
        </div>
      )}

      <div className="grid grid-cols-12 gap-5">
        {/* Pages panel */}
        <div className="col-span-12 lg:col-span-2 space-y-2">
          <div className="flex items-center justify-between px-1">
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Pages</span>
            <button onClick={addPage} className="text-muted-foreground hover:text-primary" data-testid="add-page-button"><Plus className="w-4 h-4" /></button>
          </div>
          {funnel.pages.map((p, i) => (
            <div key={p.id} onClick={() => { setPageId(p.id); setSelBlock(null); }}
              className={`group flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${p.id === pageId ? "bg-primary/15 text-primary" : "hover:bg-secondary/50 text-muted-foreground"}`}
              data-testid={`page-tab-${p.slug}`}>
              <span className="flex-1 truncate">{p.name}</span>
              <button onClick={(e) => { e.stopPropagation(); movePage(i, -1); }} className="opacity-0 group-hover:opacity-100"><ChevronUp className="w-3.5 h-3.5" /></button>
              <button onClick={(e) => { e.stopPropagation(); movePage(i, 1); }} className="opacity-0 group-hover:opacity-100"><ChevronDown className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>

        {/* Canvas */}
        <div className="col-span-12 lg:col-span-6">
          <Card className="bg-card border-border/70 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border/60 flex items-center gap-2 text-xs text-muted-foreground">
              <FileText className="w-3.5 h-3.5" /> /f/…/{page.slug}
              <button onClick={() => delPage(page.id)} className="ml-auto hover:text-destructive" data-testid="delete-page-button"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className="p-6 space-y-4 min-h-[420px]" style={{ background: page.settings?.bg || "#0b1220" }}>
              {(page.blocks || []).length === 0 && <p className="text-center text-white/40 py-16 text-sm">Empty page — add a block below.</p>}
              {(page.blocks || []).map((b, i) => (
                <div key={b.id} onClick={() => setSelBlock(b.id)}
                  className={`relative rounded-lg transition-all ${selBlock === b.id ? "ring-2 ring-primary" : "ring-1 ring-transparent hover:ring-white/15"}`}
                  data-testid={`canvas-block-${b.type}`}>
                  <div className="absolute -top-2 -right-2 z-10 flex gap-1 opacity-0 hover:opacity-100 [div:hover>&]:opacity-100" style={{ opacity: selBlock === b.id ? 1 : undefined }}>
                    <button onClick={(e) => { e.stopPropagation(); moveBlock(i, -1); }} className="w-6 h-6 rounded bg-secondary border border-border flex items-center justify-center"><ChevronUp className="w-3 h-3" /></button>
                    <button onClick={(e) => { e.stopPropagation(); moveBlock(i, 1); }} className="w-6 h-6 rounded bg-secondary border border-border flex items-center justify-center"><ChevronDown className="w-3 h-3" /></button>
                    <button onClick={(e) => { e.stopPropagation(); delBlock(b.id); }} className="w-6 h-6 rounded bg-secondary border border-border flex items-center justify-center hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
                  </div>
                  <div className="pointer-events-none p-2 text-white"><BlockRenderer block={b} brand={brand} /></div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-border/60">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full gap-2 border-dashed" data-testid="add-block-button"><Plus className="w-4 h-4" />Add block</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  {BLOCKS.map((b) => (
                    <DropdownMenuItem key={b.type} onClick={() => addBlock(b.type)} className="gap-2 cursor-pointer" data-testid={`add-block-${b.type}`}>
                      <b.icon className="w-4 h-4 text-primary" />{b.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </Card>
        </div>

        {/* Properties */}
        <div className="col-span-12 lg:col-span-4">
          <Card className="bg-card border-border/70 p-5">
            <Tabs defaultValue="block">
              <TabsList className="w-full"><TabsTrigger value="block" className="flex-1" data-testid="props-block-tab">Block</TabsTrigger><TabsTrigger value="page" className="flex-1" data-testid="props-page-tab">Page & SEO</TabsTrigger></TabsList>
              <TabsContent value="block" className="mt-4">
                {block ? <BlockProps block={block} update={(p) => updateBlock(block.id, p)} pages={funnel.pages} />
                  : <p className="text-sm text-muted-foreground py-8 text-center">Select a block to edit it.</p>}
              </TabsContent>
              <TabsContent value="page" className="mt-4 space-y-3">
                <Field label="Page name"><Input value={page.name} onChange={(e) => updatePage({ name: e.target.value })} data-testid="page-name-input" /></Field>
                <Field label="URL slug"><Input value={page.slug} onChange={(e) => updatePage({ slug: e.target.value.replace(/\s+/g, "-").toLowerCase() })} data-testid="page-slug-input" /></Field>
                <Field label="Background colour"><Input type="color" value={page.settings?.bg || "#0b1220"} onChange={(e) => updatePage({ settings: { ...page.settings, bg: e.target.value } })} className="h-10 p-1" data-testid="page-bg-input" /></Field>
                <div className="border-t border-border/60 pt-3 space-y-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">SEO</p>
                  <Field label="Title"><Input value={page.seo?.title || ""} onChange={(e) => updatePage({ seo: { ...page.seo, title: e.target.value } })} data-testid="seo-title-input" /></Field>
                  <Field label="Meta description"><Textarea value={page.seo?.description || ""} onChange={(e) => updatePage({ seo: { ...page.seo, description: e.target.value } })} className="min-h-[70px]" data-testid="seo-desc-input" /></Field>
                  <Field label="OG image URL"><Input value={page.seo?.og_image || ""} onChange={(e) => updatePage({ seo: { ...page.seo, og_image: e.target.value } })} data-testid="seo-og-input" /></Field>
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return <Card className="p-3 bg-card border-border/70"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p><p className="text-2xl font-display font-bold mt-0.5">{value}</p></Card>;
}
function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}

function BlockProps({ block, update, pages }) {
  const p = block.props || {};
  const AlignSel = () => (
    <Field label="Align"><Select value={p.align || "center"} onValueChange={(v) => update({ align: v })}>
      <SelectTrigger data-testid="prop-align"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Center</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
    </Select></Field>
  );
  switch (block.type) {
    case "heading":
      return (<div className="space-y-3">
        <Field label="Text"><Textarea value={p.text || ""} onChange={(e) => update({ text: e.target.value })} data-testid="prop-text" /></Field>
        <Field label="Size"><Select value={p.size || "xl"} onValueChange={(v) => update({ size: v })}><SelectTrigger data-testid="prop-size"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="sm">Small</SelectItem><SelectItem value="md">Medium</SelectItem><SelectItem value="lg">Large</SelectItem><SelectItem value="xl">Extra large</SelectItem></SelectContent></Select></Field>
        <AlignSel /></div>);
    case "text":
      return (<div className="space-y-3"><Field label="Text"><Textarea value={p.text || ""} onChange={(e) => update({ text: e.target.value })} className="min-h-[100px]" data-testid="prop-text" /></Field><AlignSel /></div>);
    case "image":
      return (<div className="space-y-3">
        <Field label="Image URL"><Input value={p.url || ""} onChange={(e) => update({ url: e.target.value })} data-testid="prop-url" /></Field>
        <Field label="Width (px)"><Input type="number" value={p.width || 640} onChange={(e) => update({ width: parseInt(e.target.value) || 640 })} data-testid="prop-width" /></Field><AlignSel /></div>);
    case "video":
      return <Field label="Video URL (YouTube/Vimeo)"><Input value={p.url || ""} onChange={(e) => update({ url: e.target.value })} data-testid="prop-url" /></Field>;
    case "button":
      return (<div className="space-y-3">
        <Field label="Label"><Input value={p.label || ""} onChange={(e) => update({ label: e.target.value })} data-testid="prop-label" /></Field>
        <Field label="Action"><Select value={p.action || "link"} onValueChange={(v) => update({ action: v })}><SelectTrigger data-testid="prop-action"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="link">Open link</SelectItem><SelectItem value="submit">Submit form</SelectItem></SelectContent></Select></Field>
        {p.action !== "submit" && <Field label="URL"><Input value={p.url || ""} onChange={(e) => update({ url: e.target.value })} data-testid="prop-url" /></Field>}
        <AlignSel /></div>);
    case "countdown":
      return (<div className="space-y-3">
        <Field label="Label"><Input value={p.label || ""} onChange={(e) => update({ label: e.target.value })} data-testid="prop-label" /></Field>
        <Field label="Minutes"><Input type="number" value={p.minutes || 15} onChange={(e) => update({ minutes: parseInt(e.target.value) || 0 })} data-testid="prop-minutes" /></Field></div>);
    case "spacer":
      return <Field label="Height (px)"><Input type="number" value={p.height || 40} onChange={(e) => update({ height: parseInt(e.target.value) || 0 })} data-testid="prop-height" /></Field>;
    case "form":
      return (<div className="space-y-3">
        <Field label="Form title"><Input value={p.title || ""} onChange={(e) => update({ title: e.target.value })} data-testid="prop-title" /></Field>
        <Field label="Submit button label"><Input value={p.submit_label || ""} onChange={(e) => update({ submit_label: e.target.value })} data-testid="prop-submit-label" /></Field>
        <Field label="Tag contact on submit"><Input value={p.tag_on_submit || ""} onChange={(e) => update({ tag_on_submit: e.target.value })} placeholder="opt-in" data-testid="prop-tag" /></Field>
        <Field label="Redirect to page (slug)"><Select value={p.redirect_slug || "__next"} onValueChange={(v) => update({ redirect_slug: v === "__next" ? "" : v })}>
          <SelectTrigger data-testid="prop-redirect"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="__next">Next page</SelectItem>{pages.map((pg) => <SelectItem key={pg.slug} value={pg.slug}>{pg.name}</SelectItem>)}</SelectContent></Select></Field>
        <div className="border-t border-border/60 pt-3">
          <p className="text-xs text-muted-foreground mb-2">Fields</p>
          {(p.fields || []).map((f, i) => (
            <div key={i} className="flex items-center gap-2 mb-2">
              <Input value={f.label} onChange={(e) => update({ fields: p.fields.map((x, k) => k === i ? { ...x, label: e.target.value } : x) })} className="h-8" data-testid={`form-field-${i}`} />
              <Input value={f.key} onChange={(e) => update({ fields: p.fields.map((x, k) => k === i ? { ...x, key: e.target.value } : x) })} className="h-8 w-24 font-mono text-xs" placeholder="key" />
              <button onClick={() => update({ fields: p.fields.filter((_, k) => k !== i) })} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <Button variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={() => update({ fields: [...(p.fields || []), { key: `field_${(p.fields || []).length + 1}`, label: "New field", type: "text" }] })} data-testid="add-form-field">
            <Plus className="w-3.5 h-3.5" />Add field</Button>
        </div></div>);
    default:
      return <p className="text-sm text-muted-foreground">No settings for this block.</p>;
  }
}
